import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useAuth } from '@gojiraf/auth'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  CancelButton,
  Container,
  InputContainer,
  QuotedMessagePreview,
  SendButton,
  Input,
} from './ChatInput.styles'
import {
  clearQuotedMessage,
  selectDisabledByCooldown,
  selectQuotedMessage,
  setProductTourActive,
  selectIsChatRegistered,
} from '../../reducers/uiSlice'
import { setShowAskNameDialog } from '../../reducers/uiSlice'
import { CustomerRoles } from '../../constants/customerRoles'
import { DefaultBuyerName } from '@gojiraf/auth'
import { useViews } from 'state'
import { useAmplifyChat } from '@gojiraf/chat'
import { selectTermsAndConditionsAccepted } from '../../reducers/callSlice'
import { useSearchParams } from 'react-router-dom'

const MAX_CHARS = 200
const MAX_CHARS_MODERATOR = 280

const ChatInput = ({ isDesktop = false, sendMessage }) => {
  const { t } = useTranslation()
  const [text, setText] = useState(null)
  const inputRef = useRef(null)
  const dispatch = useDispatch()
  const tycAccepted = useSelector(selectTermsAndConditionsAccepted)
  const [searchParams] = useSearchParams()
  const isDemo = searchParams.get('demo') === 'true'
  const {
    state: { isUserMuted },
  } = useAmplifyChat()
  const chatCooldown = useSelector(selectDisabledByCooldown)
  const quotedMessage = useSelector(selectQuotedMessage)
  const { user } = useAuth()
  const isChatRegistered = useSelector(selectIsChatRegistered)
  const { send: sendViews } = useViews()
  const maxLengthMessage = user.role === CustomerRoles.MODERATOR ? MAX_CHARS_MODERATOR : MAX_CHARS

  const handleInput = (e) => {
    if (!isUserMuted && !isChatRegistered) {
      e.currentTarget.blur()
      isDesktop
        ? dispatch(setShowAskNameDialog({ open: true, allowCancel: true }))
        : sendViews({ type: 'SHOW_JOIN_CHAT' })
    }
  }

  const handleChange = (e) => {
    setText(e.target.value)
  }

  const isValidMessage = (message) => {
    if (!/\S/.test(message) || message.length < 1) {
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    inputRef.current.focus()
    if (!isValidMessage(text)) return
    setText('')
    await sendMessage({ message: text })
  }

  const onMessageCancel = () => {
    dispatch(clearQuotedMessage())
  }

  const chatInputPlaceHolder = () => {
    if (isUserMuted) return t('chat.chatMuted')
    if (chatCooldown) return t('chat.chatCooldown')

    return t('chat.inputPlaceholder')
  }

  const handleKeyPress = async (event) => {
    if (event.key == 'Enter') {
      await handleSubmit()
    }
  }

  useEffect(() => {
    if (isDesktop && user.name !== DefaultBuyerName) {
      inputRef.current.focus()
    }
    if (!isDesktop && quotedMessage) inputRef.current.focus()
  }, [quotedMessage])

  useEffect(() => {
    // Activar el tour solo si no se ha ejecutado antes
    const tourCompleted = sessionStorage.getItem('productTourCompleted') === 'true'
    if (tycAccepted && isDemo && !tourCompleted) {
      dispatch(setProductTourActive(true))
    }
  }, [tycAccepted])

  return (
    <Container isDesktop={isDesktop} data-test="input-container">
      <InputContainer
        data-test="input-box"
        data-tour="chat-input"
        isDesktop={isDesktop}
        onClick={(e) => handleInput(e)}
      >
        {quotedMessage && (
          <QuotedMessagePreview
            isDesktop={isDesktop}
          >{`@${quotedMessage.user.name}`}</QuotedMessagePreview>
        )}
        <Input
          id="chat-input"
          data-test="input-chat"
          ref={inputRef}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          name="message"
          value={text}
          placeholder={chatInputPlaceHolder()}
          maxLength={maxLengthMessage}
          disabled={isUserMuted || chatCooldown}
          onClick={handleInput}
          isDesktop={isDesktop}
          autoComplete="off"
        />
        {quotedMessage && (
          <CancelButton data-test="send" isDesktop={isDesktop} onClick={onMessageCancel} />
        )}
        <SendButton
          id="chat-send-message-button"
          onClick={handleSubmit}
          disabled={isUserMuted || chatCooldown}
          data-test="chat-send-message-button"
          isDesktop={isDesktop}
        />
      </InputContainer>
    </Container>
  )
}

ChatInput.propTypes = {
  isDesktop: PropTypes.bool,
  sendMessage: PropTypes.func,
}

export { ChatInput }
